import { notification } from 'antd';
import { useEffect, useState } from 'react';
import {
  Apple,
  Arachnid,
  Ball,
  Banana,
  Bird,
  Bone,
  Branch,
  Burrow,
  Camera,
  Carrot,
  Cat,
  Cheese,
  CircleError,
  CircleExclamationSolid,
  CircleSuccess,
  Coral,
  Dog,
  FarmAnimal,
  Feeder1,
  Feeder2,
  Feeder3,
  Fence,
  Ferret,
  Fish,
  Horse,
  Insect,
  Leaf,
  Lettuce,
  Monkey,
  Mouse,
  Pig,
  Rabbit,
  Reptile,
  Rodent,
  RunningWheel,
  Seaweed,
  SewingThread,
  Snake,
  SpiderWeb,
  Swing,
  Teddy,
  Tree,
  TriangleExclamationSolid,
  Turtle,
  XMarkSolid,
} from '../../utils/icons/index';
import { useAnimalHeaven } from '../../context/animalHeaven.provider';
import useApi from '../api';
import {
  setAnimalSizes,
  setLoading,
  setPetCharacteristics,
  setPetTypes,
} from '../../context/animalHeaven.actions';
import { getAnimalSizes, getPetTypes, getPetCharacteristics } from '../actions';
import { AnimalSize, PetType, PetCharacteristic } from '../../context/animalHeaven.types';

const getIsMobile = () => window.innerWidth < 768;

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(getIsMobile());

  useEffect(() => {
    const onResize = () => {
      if ((isMobile && !getIsMobile()) || (!isMobile && getIsMobile())) setIsMobile(getIsMobile());
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isMobile]);

  return isMobile;
};

export const notificationMessage = ({
  message,
  type,
}: {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}) => {
  const isMobile = getIsMobile();
  const commonIconStyle = 'flex relative top-[1px] mr-[8px] [&>svg]:w-[20px] [&>svg]:h-[20px]';
  let icon;

  if (type === 'success') {
    icon = (
      <div className={`${commonIconStyle} [&>svg>path]:fill-success`}>
        <CircleSuccess />
      </div>
    );
  } else if (type === 'error') {
    icon = (
      <div className={`${commonIconStyle} [&>svg>path]:fill-danger`}>
        <CircleError />
      </div>
    );
  } else if (type === 'info') {
    icon = (
      <div className={`${commonIconStyle} [&>svg>path]:fill-info`}>
        <CircleExclamationSolid />
      </div>
    );
  } else {
    icon = (
      <div className={`${commonIconStyle} [&>svg>path]:fill-warning`}>
        <TriangleExclamationSolid />
      </div>
    );
  }

  notification[type]({
    message: (
      <div className="flex">
        {icon}
        <p>{message}</p>
      </div>
    ),
    placement: isMobile ? 'top' : 'topRight',
    closeIcon: isMobile ? (
      false
    ) : (
      <div className="[&>svg>path]:fill-text2">
        <XMarkSolid />
      </div>
    ),
    icon: <></>,
  });
};

export const usePetTypes = () => {
  const { animalHeavenDispatch, loading, petTypes } = useAnimalHeaven();
  const { api } = useApi();

  useEffect(() => {
    if (!petTypes.length && !loading.petTypes) {
      animalHeavenDispatch(setLoading({ key: 'petTypes', value: true }));
      api(getPetTypes())
        .then((resp) => {
          if (resp.status === 200) {
            const idWithInfo: {
              [key: number]: {
                name: string;
                icon: JSX.Element;
                foodIcon: JSX.Element;
                toyOrPlaceIcon: JSX.Element;
              };
            } = {
              1: {
                name: 'common.petTypes.dog',
                icon: <Dog />,
                foodIcon: <Feeder1 />,
                toyOrPlaceIcon: <Bone />,
              },
              2: {
                name: 'common.petTypes.cat',
                icon: <Cat />,
                foodIcon: <Feeder2 />,
                toyOrPlaceIcon: <SewingThread />,
              },
              3: {
                name: 'common.petTypes.bird',
                icon: <Bird />,
                foodIcon: <Feeder3 />,
                toyOrPlaceIcon: <Swing />,
              },
              4: {
                name: 'common.petTypes.arachnid',
                icon: <Arachnid />,
                foodIcon: <Insect />,
                toyOrPlaceIcon: <SpiderWeb />,
              },
              5: {
                name: 'common.petTypes.turtle',
                icon: <Turtle />,
                foodIcon: <Lettuce />,
                toyOrPlaceIcon: <Camera />,
              },
              6: {
                name: 'common.petTypes.snake',
                icon: <Snake />,
                foodIcon: <Mouse />,
                toyOrPlaceIcon: <Tree />,
              },
              7: {
                name: 'common.petTypes.reptile',
                icon: <Reptile />,
                foodIcon: <Insect />,
                toyOrPlaceIcon: <Branch />,
              },
              8: {
                name: 'common.petTypes.rabbit',
                icon: <Rabbit />,
                foodIcon: <Carrot />,
                toyOrPlaceIcon: <Burrow />,
              },
              9: {
                name: 'common.petTypes.rodent',
                icon: <Rodent />,
                foodIcon: <Cheese />,
                toyOrPlaceIcon: <RunningWheel />,
              },
              10: {
                name: 'common.petTypes.pig',
                icon: <Pig />,
                foodIcon: <Apple />,
                toyOrPlaceIcon: <Fence />,
              },
              11: {
                name: 'common.petTypes.horse',
                icon: <Horse />,
                foodIcon: <Carrot />,
                toyOrPlaceIcon: <Ball />,
              },
              12: {
                name: 'common.petTypes.ferret',
                icon: <Ferret />,
                foodIcon: <Feeder1 />,
                toyOrPlaceIcon: <Teddy />,
              },
              13: {
                name: 'common.petTypes.monkey',
                icon: <Monkey />,
                foodIcon: <Banana />,
                toyOrPlaceIcon: <Tree />,
              },
              14: {
                name: 'common.petTypes.fish',
                icon: <Fish />,
                foodIcon: <Seaweed />,
                toyOrPlaceIcon: <Coral />,
              },
              15: {
                name: 'common.petTypes.farmAnimal',
                icon: <FarmAnimal />,
                foodIcon: <Leaf />,
                toyOrPlaceIcon: <Fence />,
              },
            };

            const newPetTypes: PetType[] = [];
            resp.data.petTypes.forEach((e: { id: number }) => {
              newPetTypes.push({
                foodIcon: idWithInfo[e.id].foodIcon,
                id: e.id,
                name: idWithInfo[e.id].name,
                petIcon: idWithInfo[e.id].icon,
                toyOrPlaceIcon: idWithInfo[e.id].toyOrPlaceIcon,
              });
            });
            animalHeavenDispatch(setPetTypes(newPetTypes));
          }
        })
        .finally(() => animalHeavenDispatch(setLoading({ key: 'petTypes', value: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalHeavenDispatch, api, petTypes.length]);
};

export const useAnimalSizes = () => {
  const { animalHeavenDispatch, animalSizes, loading } = useAnimalHeaven();
  const { api } = useApi();

  useEffect(() => {
    if (!animalSizes.length && !loading.animalSizes) {
      animalHeavenDispatch(setLoading({ key: 'animalSizes', value: true }));
      api(getAnimalSizes())
        .then((resp) => {
          if (resp.status === 200) {
            const idWithInfo: {
              [key: number]: {
                name: string;
              };
            } = {
              1: {
                name: 'common.animalSizes.small',
              },
              2: {
                name: 'common.animalSizes.medium',
              },
              3: {
                name: 'common.animalSizes.big',
              },
            };

            const newAnimalSizes: AnimalSize[] = resp.data.animalSizes.map((e: { id: number }) => ({
              id: e.id,
              name: idWithInfo[e.id].name,
            }));

            newAnimalSizes.sort((a: AnimalSize, b: AnimalSize) => a.id - b.id);
            animalHeavenDispatch(setAnimalSizes(newAnimalSizes));
          }
        })
        .finally(() => animalHeavenDispatch(setLoading({ key: 'animalSizes', value: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalHeavenDispatch, animalSizes.length, api]);
};

export const usePetCharacteristics = () => {
  const { animalHeavenDispatch, loading, petCharacteristics } = useAnimalHeaven();
  const { api } = useApi();

  useEffect(() => {
    if (!petCharacteristics.length && !loading.petCharacteristics) {
      animalHeavenDispatch(setLoading({ key: 'petCharacteristics', value: true }));
      api(getPetCharacteristics())
        .then((resp) => {
          if (resp.status === 200) {
            const idWithInfo: {
              [key: number]: {
                name: string;
              };
            } = {
              1: { name: 'common.petCharacteristics.highActivity' },
              2: { name: 'common.petCharacteristics.needsTreatment' },
              3: { name: 'common.petCharacteristics.PPPBreed' },
              4: { name: 'common.petCharacteristics.suitableForChildren' },
              5: { name: 'common.petCharacteristics.notSuitableForChildren' },
              6: { name: 'common.petCharacteristics.territorial' },
              7: { name: 'common.petCharacteristics.puppy' },
              8: { name: 'common.petCharacteristics.trained' },
              9: { name: 'common.petCharacteristics.quiet' },
              10: { name: 'common.petCharacteristics.family' },
              11: { name: 'common.petCharacteristics.loving' },
              12: { name: 'common.petCharacteristics.specialCare' },
            };

            const newPetCharacteristics: PetCharacteristic[] = resp.data.petCharacteristics.map(
              (e: { id: number }) => ({
                id: e.id,
                name: idWithInfo[e.id].name,
              })
            );

            newPetCharacteristics.sort((a: PetCharacteristic, b: PetCharacteristic) => a.id - b.id);
            animalHeavenDispatch(setPetCharacteristics(newPetCharacteristics));
          }
        })
        .finally(() =>
          animalHeavenDispatch(setLoading({ key: 'petCharacteristics', value: false }))
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalHeavenDispatch, api, petCharacteristics.length]);
};
